/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Grid, TextField, Typography } from '@mui/material';

import { ToolsDetails } from '../../components/custom';
import { LayoutBase } from '../../layouts';
import { IProfessionals, ProfessionalsServices } from '../../services/api/professionals/ProfessionalsServices';
import { CustomAlert, Loadin, VAutoCompletProfissional } from '../../components/shared';

export const DetailsProfessionals: React.FC = () => {

    const url = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const [alertInfo, setAlertInfo] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    const [hasSuperiorOptions, setHasSuperiorOptions] = useState(false);

    const initialData: IProfessionals = {
        nome: '',
        registro_pessoal: '',
        email: '',
        id_empresa: '',
        id_empreendimento: '',
        id_barragem: '',
        role: '',
        status: false,
        id_superior: '',
        registro_profissional: '',
        telefone: '',
        celular: '',
        curriculum: '',
        company_name: '',
        registro: '',
        professional_photo: '',
        arquivo_registro_profissional: '',
        arquivo_curriculum: '',
        uuid: '',
        funcao: '',
    };

    const [data, setData] = useState<IProfessionals>(initialData);

    const [validations, setValidations] = useState<{ [key: string]: boolean }>({
        nome: false,
        registro_pessoal: false,
        email: false,
        role: false,
        id_superior: false
    });

    const resetForm = () => {
        setData(initialData);
        setValidations({
            nome: false,
            registro_pessoal: false,
            email: false,
            role: false,
            id_superior: false
        });
    };

    async function validateData(data: IProfessionals) {
        const newValidations = {
            nome: !data.nome,
            registro_pessoal: !data.registro_pessoal,
            email: !data.email,
            role: !data.role,
            id_superior: hasSuperiorOptions && !data.id_superior
        };
        setValidations(newValidations);
        const isValid = Object.values(newValidations).some(value => value);
        return !isValid;
    }

    useEffect(() => {
        setIsLoading(true);
        if (url && url.uuid && url.id) {
            if (url.uuid !== 'new') {
                ProfessionalsServices.getById(url.uuid)
                    .then((result) => {
                        if (result instanceof Error) {
                            alert(result.message);
                        } else {
                            setData(result);
                        }
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            } else {
                setIsLoading(false);
            }
        }
    }, [url]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { id, value, type, checked } = e.target;

        const convertedValue = type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) : value;
        setData(prevData => ({
            ...prevData,
            [id]: convertedValue
        }));
    };

    async function handleSave() {
        const isValid = await validateData(data);

        if (isValid) {
            setAlertInfo({ open: false, message: '', severity: 'success' });
            if (url && url.uuid && url.id) {
                if (url.uuid == 'new') {
                    delete data.uuid;
                    delete data.id_empresa;
                    delete data.id_barragem;
                    delete data.id_empreendimento;
                    delete data.status;
                    const response = await ProfessionalsServices.create(url.id, data);
                    if (response instanceof Error) {
                        setAlertInfo({ open: true, message: `Erro ao criar. Error: ${response.message}`, severity: 'error' });
                    } else {
                        setAlertInfo({ open: true, message: 'Registro Criado', severity: 'success' });
                        resetForm();
                        setTimeout(() => {
                            navigate(`/information_dam/${url.id}/organization_chart`);
                        }, 2000);
                    }
                } else {
                    const response = await ProfessionalsServices.upgrade(url.uuid, data);
                    setAlertInfo({ open: true, message: 'Registro atualizado', severity: 'success' });
                    setTimeout(() => {
                        navigate(`/information_dam/${url.id}/organization_chart/professionals`);
                    }, 2000);
                }
            }
        } else {
            let errorMessage = 'Por favor, preencha todos os campos obrigatórios';
            if (hasSuperiorOptions && !data.id_superior) {
                errorMessage += ', incluindo a seleção de um Superior';
            }
            errorMessage += '.';

            setAlertInfo({
                open: true,
                message: errorMessage,
                severity: 'error'
            });
        }
    }

    const handleNew = () => {
        window.location.reload();
        navigate(`/information_dam/${url.id}/organization_chart/professionals/uuid=novo`);
    };

    async function handleDelete() {
        if (url && url.uuid && url.id) {
            if (confirm('Deseja apagar?')) {
                ProfessionalsServices.delet(url.uuid)
                    .then(result => {
                        if (result instanceof Error) {
                            alert(result.message);
                        } else {
                            if (url && url.uuid && url.id) {
                                ProfessionalsServices.delet(url.uuid);
                                navigate(`/information_dam/${url.id}/organization_chart`);
                                alert('Registro Apagado!');
                            }
                        }
                    });
            }
        }
    }

    function handleUserChange(user: string) {
        setData(prevData => ({
            ...prevData,
            id_superior: user
        }));
    }

    const handleSuperiorOptionsLoaded = (hasOptions: boolean) => {
        setHasSuperiorOptions(hasOptions);
    };

    if (isLoading) {
        return (
            <Loadin />
        );
    }

    return (
        <LayoutBase title={url.uuid === 'new' ? 'Novo Usuário' : data.nome}
            tollBar={
                <ToolsDetails
                    showDeletButton={url.uuid !== 'new'}
                    showNewButton={url.uuid !== 'new'}
                    onClickNew={() => handleNew}
                    onClickSave={() => handleSave()}
                    onClickDelet={() => handleDelete()}
                />
            }
        >
            <Box margin={0} display={'flex'} flexDirection={'column'}>
                <Grid container direction={'column'} padding={2} spacing={2}>
                    <Grid item >
                        <Typography variant='h4'>Ficha de Profissional</Typography>
                    </Grid>
                    <Grid container item direction={'row'} spacing={2}>
                        <Grid item xs={6}>
                            <TextField label='Nome' id='nome' fullWidth
                                value={data.nome || ''}
                                onChange={handleChange}
                                error={validations.nome}
                                required
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth id='email' label='E-mail'
                                value={data.email || ''}
                                error={validations.email}
                                onChange={handleChange}
                                required />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField label='Registro' id='registro_pessoal'
                                fullWidth
                                value={data.registro_pessoal || ''}
                                onChange={handleChange}
                                error={validations.registro_pessoal}
                                required />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth id='role' label='Cargo'
                                value={data.role || ''}
                                error={validations.role}
                                onChange={handleChange}
                                required />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField label='Telefone' id='telefone' fullWidth value={data.telefone || ''} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth id='celular' label='Celular' value={data.celular || ''} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField label='Curriculo' id='curriculum' fullWidth value={data.curriculum || ''} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth id='company_name' label='Nome da Empresa' value={data.company_name || ''} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth id='funcao' label='Função' value={data.funcao || ''} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={6}>
                            <VAutoCompletProfissional
                                error={validations.id_superior}
                                uuidCompany={url.id}
                                onUserChange={(user) => handleUserChange(user)}
                                initialValue={data.id_superior}
                                userId={url.uuid}
                                name='Superior'
                                onOptionsLoaded={handleSuperiorOptionsLoaded}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
            {alertInfo.open &&
                <CustomAlert
                    severity={alertInfo.severity}
                    message={alertInfo.message}
                />
            }
        </LayoutBase>
    );
};